import { useEffect, useRef, useState, useCallback } from "react";
import Globe from "react-globe.gl";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, MapPin, ArrowRight, X, Mail } from "lucide-react";
import { Container, Section, SectionHeading, Reveal, Eyebrow, LeafBackdrop } from "@/components/common";
import { CTABand } from "@/components/ui-extras";
import { MARKETS, ORIGIN, COMPANY } from "@/data/content";

const WORLD_VIEW = { lat: 18, lng: 40, altitude: 2.6 };

const MarketsServed = () => {
  const globeRef = useRef(null);
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 520 });
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [ready, setReady] = useState(false);

  // responsive sizing
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth;
      const h = Math.max(460, Math.min(760, Math.round(w * 0.58)));
      setDims({ w, h });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // initial globe config
  useEffect(() => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.45;
    controls.enableZoom = true;
    globeRef.current.pointOfView(WORLD_VIEW, 0);
    setReady(true);
  }, []);

  // build points (flat glowing dots; gold = primary markets, soft blue = expansion)
  const points = MARKETS.flatMap((m) =>
    m.pins.map((p) => ({ name: p.n, lat: p.lat, lng: p.lng, regionId: m.id, region: m.name, tier: m.tier }))
  );

  const dotColor = (tier) => (tier === "expansion" ? "#7FB6DA" : "#E3C878");

  const ringsData = selected
    ? MARKETS.find((m) => m.id === selected.id).pins.map((p) => ({ lat: p.lat, lng: p.lng, tier: selected.tier }))
    : hovered
    ? [{ lat: hovered.lat, lng: hovered.lng, tier: hovered.tier }]
    : [];

  const focusRegion = useCallback((m) => {
    setSelected(m);
    if (globeRef.current) {
      globeRef.current.controls().autoRotate = false;
      globeRef.current.pointOfView({ lat: m.lat, lng: m.lng, altitude: m.altitude || 1.5 }, 1400);
    }
  }, []);

  const reset = useCallback(() => {
    setSelected(null);
    if (globeRef.current) {
      globeRef.current.controls().autoRotate = true;
      globeRef.current.pointOfView(WORLD_VIEW, 1400);
    }
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden hero-navy-gradient">
        <LeafBackdrop size={560} className="left-1/2 top-2 -translate-x-1/2 opacity-25" />
        <LeafBackdrop size={320} className="-left-24 top-24 opacity-20" color="rgba(65,225,154,0.07)" />
        <Container wide className="pt-14 pb-6 sm:pt-16">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
            <Eyebrow onNavy>Markets Served</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-[-0.02em] text-white">
              An Interactive Global Trade Network
            </h1>
            <p className="mt-5 max-w-2xl font-sans text-base sm:text-lg leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>
              Prince Trading Impex serves and develops trade relationships across the Middle East, Africa, South-East Asia, and an expanding worldwide network. Click a region to explore active and target cities.
            </p>
          </div>
        </Container>

        {/* Globe hero + panel below */}
        <Container wide className="pb-16">
          <div className="flex flex-col gap-8">
            {/* Globe (hero) */}
            <div className="relative w-full mx-auto rounded-[var(--radius)] overflow-hidden" style={{ border: "1px solid rgba(58,187,176,0.25)", background: "radial-gradient(circle at 50% 35%, rgba(58,187,176,0.12), rgba(2,6,6,0) 62%)" }}>
              <div ref={wrapRef} data-testid="markets-globe" className="w-full flex items-center justify-center">
                <Globe
                  ref={globeRef}
                  width={dims.w}
                  height={dims.h}
                  backgroundColor="rgba(0,0,0,0)"
                  globeImageUrl="https://unpkg.com/three-globe/example/img/earth-dark.jpg"
                  bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
                  showAtmosphere={true}
                  atmosphereColor="#3ABBB0"
                  atmosphereAltitude={0.2}
                  pointsData={points}
                  pointLat="lat"
                  pointLng="lng"
                  pointAltitude={0.012}
                  pointRadius={(d) => (d.tier === "expansion" ? 0.42 : 0.52)}
                  pointColor={(d) => dotColor(d.tier)}
                  pointLabel={(d) => `<div style="font-family:sans-serif;font-size:12px;background:#020606;color:#fff;border:1px solid rgba(58,187,176,0.6);padding:4px 8px;border-radius:6px">${d.name} · ${d.region}</div>`}
                  onPointHover={(d) => setHovered(d || null)}
                  onPointClick={(d) => {
                    const m = MARKETS.find((x) => x.id === d.regionId);
                    if (m) focusRegion(m);
                  }}
                  ringsData={ringsData}
                  ringColor={(r) => (t) => (r.tier === "expansion" ? `rgba(127,182,218,${1 - t})` : `rgba(227,200,120,${1 - t})`)}
                  ringMaxRadius={3.5}
                  ringPropagationSpeed={2.6}
                  ringRepeatPeriod={850}
                />
              </div>
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-sans text-sm" style={{ color: "var(--pt-muted-on-navy)" }}>Loading globe…</span>
                </div>
              )}
              {selected && (
                <button
                  onClick={reset}
                  data-testid="markets-reset-button"
                  className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full px-4 py-2 font-sans text-xs font-semibold text-white"
                  style={{ background: "rgba(2,6,6,0.6)", border: "1px solid rgba(58,187,176,0.5)" }}
                >
                  <RotateCcw size={14} /> Reset View
                </button>
              )}
              {/* Legend */}
              <div className="absolute right-4 bottom-4 flex flex-col gap-1.5 rounded-lg px-3 py-2.5" style={{ background: "rgba(2,6,6,0.6)", border: "1px solid rgba(58,187,176,0.35)" }}>
                <span className="flex items-center gap-2 font-sans text-[11px] text-white"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#E3C878", boxShadow: "0 0 8px #E3C878" }} /> Primary service markets</span>
                <span className="flex items-center gap-2 font-sans text-[11px] text-white"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#7FB6DA", boxShadow: "0 0 8px #7FB6DA" }} /> Expansion markets</span>
              </div>
            </div>

            {/* Side panel below globe */}
            <div className="w-full mx-auto max-w-5xl">
              <div className="h-full rounded-[var(--radius)] p-6 sm:p-7" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(58,187,176,0.25)" }}>
                <AnimatePresence mode="wait">
                  {!selected ? (
                    <motion.div key="list" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <h3 className="font-serif text-2xl text-white">Select a Market Region</h3>
                      <p className="mt-2 font-sans text-sm" style={{ color: "var(--pt-muted-on-navy)" }}>
                        Click a region below or a pin on the globe to zoom in and view active cities, product focus, and trade capabilities.
                      </p>
                      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="markets-region-list">
                        {MARKETS.map((m) => (
                          <button
                            key={m.id}
                            data-testid="markets-region-button"
                            onClick={() => focusRegion(m)}
                            className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-left font-sans text-sm text-white transition-colors"
                            style={{ border: "1px solid rgba(58,187,176,0.3)", background: "rgba(58,187,176,0.06)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(58,187,176,0.16)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(58,187,176,0.06)")}
                          >
                            <MapPin size={15} color="#41E19A" /> {m.name}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div key={selected.id} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} data-testid="markets-detail-panel">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Eyebrow onNavy>Market Region</Eyebrow>
                          <h3 className="mt-2 font-serif text-3xl text-white">{selected.name}</h3>
                        </div>
                        <button onClick={reset} aria-label="Close" className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ border: "1px solid rgba(58,187,176,0.4)" }}>
                          <X size={16} />
                        </button>
                      </div>
                      <p className="mt-4 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>{selected.note}</p>

                      <p className="mt-5 font-sans text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: "#41E19A" }}>Product Focus</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selected.focus.map((f) => (
                          <span key={f} className="rounded-full px-3 py-1 font-sans text-xs text-white" style={{ background: "rgba(58,187,176,0.14)", border: "1px solid rgba(58,187,176,0.4)" }}>{f}</span>
                        ))}
                      </div>

                      <p className="mt-5 font-sans text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: "#41E19A" }}>Trade Capability</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selected.capability.map((c) => (
                          <span key={c} className="rounded-full px-3 py-1 font-sans text-xs text-white" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)" }}>{c}</span>
                        ))}
                      </div>

                      <p className="mt-5 font-sans text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: "#41E19A" }}>Active / Target Cities</p>
                      <p className="mt-2 font-sans text-[13px] leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>{selected.cities}</p>

                      <a href={`mailto:${COMPANY.email}`} className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-sans text-sm font-semibold" style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606" }}>
                        <Mail size={15} /> Email Us About {selected.name.split(" ")[0]}
                      </a>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          <p className="mt-4 text-center font-sans text-xs" style={{ color: "var(--pt-muted-on-navy)" }}>
            Interactive trade-network globe · drag to rotate, scroll to zoom, click a region to explore. Clean city highlights only — no shipping routes.
          </p>
        </Container>
        <div className="gold-hairline" />
      </section>

      {/* Region cards */}
      <Section className="bg-white">
        <Container>
          <SectionHeading center eyebrow="Regional Presence" title="Where We Trade" intro="Established demand and growing relationships across key global commodity markets." className="mb-14" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MARKETS.map((m, i) => (
              <Reveal key={m.id} delay={(i % 3) * 0.05}>
                <div className="h-full card-hover rounded-[var(--radius)] bg-paper p-6" style={{ border: "1px solid var(--pt-border-gold)" }}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "rgba(58,187,176,0.12)" }}>
                    <MapPin size={18} color="#0E8C82" />
                  </div>
                  <h3 className="mt-4 font-serif text-xl" style={{ color: "var(--pt-navy)" }}>{m.name}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>{m.note}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {m.focus.slice(0, 3).map((f) => (
                      <span key={f} className="rounded-full px-2.5 py-1 font-sans text-[11px]" style={{ background: "rgba(58,187,176,0.1)", color: "#0E8C82" }}>{f}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CTABand
        heading="Expand into new markets with a trusted partner."
        subtext="Wherever your demand is, Prince Trading Impex can structure a reliable supply relationship."
      />
    </div>
  );
};

export default MarketsServed;
